import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { getConfig } from '../../../config';
import {
  AIAgentRegistryClient,
  type UpsertAgentPersonaRequest,
  type UpsertManualRequest,
} from '../../../infrastructure/AIAgentRegistryClient';

const QUERY_KEYS = {
  registryTree: ['agent-registry-tree'],
  supervisors: ['agent-supervisors'],
  microAgents: (supervisorId: string) => ['agent-micro-agents', supervisorId],
  agent: (agentId: string) => ['agent-persona', agentId],
  manuals: (category: string) => ['agent-manuals', category],
  manual: (manualId: string) => ['agent-manual', manualId],
};

export const useAgentRegistry = () => {
  const config = getConfig();
  const client = new AIAgentRegistryClient(config.apiUrl, config.apiKey);
  const queryClient = useQueryClient();

  const registryTreeQuery = useQuery({
    queryKey: QUERY_KEYS.registryTree,
    queryFn: () => client.getRegistryTree(),
    staleTime: 60000,
  });

  const supervisorsQuery = useQuery({
    queryKey: QUERY_KEYS.supervisors,
    queryFn: () => client.listSupervisorAgents(),
    staleTime: 60000,
  });

  const useMicroAgentsQuery = (supervisorId: string) =>
    useQuery({
      queryKey: QUERY_KEYS.microAgents(supervisorId),
      queryFn: () => client.listMicroAgents(supervisorId),
      enabled: !!supervisorId,
      staleTime: 60000,
    });

  const useAgentQuery = (agentId: string) =>
    useQuery({
      queryKey: QUERY_KEYS.agent(agentId),
      queryFn: () => client.getAgentPersona(agentId),
      enabled: !!agentId,
      staleTime: 60000,
    });

  const useManualsQuery = (category: string) =>
    useQuery({
      queryKey: QUERY_KEYS.manuals(category),
      queryFn: () => client.listManualsByCategory(category),
      enabled: !!category,
      staleTime: 60000,
    });

  const useManualQuery = (manualId: string) =>
    useQuery({
      queryKey: QUERY_KEYS.manual(manualId),
      queryFn: () => client.getManual(manualId),
      enabled: !!manualId,
      staleTime: 60000,
    });

  const upsertAgentMutation = useMutation({
    mutationFn: (request: UpsertAgentPersonaRequest) => client.upsertAgentPersona(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.supervisors });
      queryClient.invalidateQueries({ queryKey: ['agent-micro-agents'] });
    },
  });

  const deleteAgentMutation = useMutation({
    mutationFn: (agentId: string) => client.deleteAgentPersona(agentId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: QUERY_KEYS.supervisors });
      queryClient.invalidateQueries({ queryKey: ['agent-micro-agents'] });
    },
  });

  const upsertManualMutation = useMutation({
    mutationFn: (request: UpsertManualRequest) => client.upsertManual(request),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-manuals'] });
    },
  });

  const deleteManualMutation = useMutation({
    mutationFn: (manualId: string) => client.deleteManual(manualId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['agent-manuals'] });
    },
  });

  return {
    registryTreeQuery,
    supervisorsQuery,
    useMicroAgentsQuery,
    useAgentQuery,
    useManualsQuery,
    useManualQuery,
    upsertAgentMutation,
    deleteAgentMutation,
    upsertManualMutation,
    deleteManualMutation,
  };
};

